import { Controller } from "@hotwired/stimulus";

const MOBILE_MENU_MEDIA_QUERY = "(max-width: 50em)";

export default class extends Controller {
  static targets = ["menu", "button", "closeArea"];

  connect() {
    this.menuElement = this.hasMenuTarget ? this.menuTarget : null;
    this.closeAreaElement = this.hasCloseAreaTarget ? this.closeAreaTarget : null;
    this.menuPortalState = new WeakMap();
    this.boundMenus = new Set();
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleMenuIdButtonClick = this.handleMenuIdButtonClick.bind(this);
    this.boundHandleMenuAction = this.handleMenuAction.bind(this);
    this.boundHandleViewportChange = this.handleViewportChange.bind(this);
    this.mobileMenuMediaQuery = window.matchMedia(MOBILE_MENU_MEDIA_QUERY);

    // NEW API: Find all buttons with menu_id data attributes (for playlist sidebar)
    this.menuIdButtons = this.element.querySelectorAll("[data-menu-id]");
    this.menuIdButtons.forEach(button => {
      button.addEventListener("click", this.boundHandleMenuIdButtonClick);
    });

    document.addEventListener("click", this.boundHandleClickOutside);
    document.addEventListener("keydown", this.boundHandleKeyDown);
    this.addViewportListener();

    this.currentOpenMenu = null;
    this.justOpenedMenu = false;
  }

  disconnect() {
    this.menuIdButtons?.forEach(button => {
      button.removeEventListener("click", this.boundHandleMenuIdButtonClick);
    });

    document.removeEventListener("click", this.boundHandleClickOutside);
    document.removeEventListener("keydown", this.boundHandleKeyDown);
    this.removeViewportListener();
    this.boundMenus.forEach(menu => {
      menu.removeEventListener("click", this.boundHandleMenuAction);
    });
    this.boundMenus.clear();

    if (this.menuElement) {
      this.hideMenu(this.menuElement);
    }

    if (this.currentOpenMenu) {
      this.hideMenu(this.currentOpenMenu);
      this.currentOpenMenu = null;
    }
  }

  // OLD API: Keep for existing components
  toggleMenu(event) {
    event.preventDefault();

    const menu = this.menuElement;
    if (!menu) return;

    // Close any menu opened via new API
    if (this.currentOpenMenu) {
      this.hideMenu(this.currentOpenMenu);
      this.currentOpenMenu = null;
    }

    const isOpening = menu.classList.contains("hidden");

    if (isOpening) {
      this.showMenu(
        menu,
        this.hasButtonTarget ? this.buttonTarget : null,
        this.positionMenuFixed
      );
    } else {
      this.hideMenu(menu);
    }
  }

  // Position menu using fixed viewport coordinates (escapes overflow:hidden ancestors)
  positionMenuFixed(button, menu) {
    const buttonRect = button.getBoundingClientRect();
    const menuWidth = 320;
    const menuHeight = menu.offsetHeight || 200; // Estimate if hidden
    const padding = 8;

    // Position to the left of the button by default
    let menuLeft = buttonRect.left - menuWidth - padding;
    let menuTop = buttonRect.top;

    // If menu would go off left edge, position to the right of button
    if (menuLeft < padding) {
      menuLeft = buttonRect.right + padding;
    }

    // Ensure menu doesn't go off right edge
    if (menuLeft + menuWidth > window.innerWidth - padding) {
      menuLeft = window.innerWidth - menuWidth - padding;
    }

    // Ensure menu doesn't go off bottom of screen
    if (menuTop + menuHeight > window.innerHeight - padding) {
      menuTop = window.innerHeight - menuHeight - padding;
    }

    // Ensure menu doesn't go off top of screen
    if (menuTop < padding) {
      menuTop = padding;
    }

    menu.style.setProperty('--menu-top', menuTop + 'px');
    menu.style.setProperty('--menu-left', menuLeft + 'px');
  }

  // NEW API: Handle buttons with data-menu-id (for playlist sidebar)
  handleMenuIdButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.currentTarget;
    const menuId = button.dataset.menuId;
    const menu = document.getElementById(menuId);
    
    if (!menu) return;
    
    // Close any menu opened via old API
    if (this.menuElement) {
      this.hideMenu(this.menuElement);
    }
    
    // Close any currently open menu via new API
    if (this.currentOpenMenu && this.currentOpenMenu !== menu) {
      this.hideMenu(this.currentOpenMenu);
    }
    
    // Toggle the clicked menu
    if (menu.classList.contains("hidden")) {
      this.showMenu(menu, button, this.positionMenu);
      this.currentOpenMenu = menu;
      
      // Set a flag to prevent immediate closing by handleClickOutside
      this.justOpenedMenu = true;
      setTimeout(() => {
        this.justOpenedMenu = false;
      }, 50);
    } else {
      this.hideMenu(menu);
      this.currentOpenMenu = null;
    }
  }

  positionMenu(button, menu) {
    const buttonRect = button.getBoundingClientRect();
    const menuContainer = this.element.querySelector('[class*="sidebar__menus"]');
    
    if (!menuContainer) return;
    
    const menuContainerRect = menuContainer.getBoundingClientRect();
    
    // Calculate position relative to the menus container (with scroll consideration)
    const relativeTop = buttonRect.top - menuContainerRect.top + menuContainer.scrollTop;
    const relativeLeft = buttonRect.left - menuContainerRect.left + menuContainer.scrollLeft;
    
    // Position menu to the left of the button (like original behavior)
    let menuLeft = relativeLeft - 320;
    
    // Check if menu would go off screen left, then position to the right
    if (buttonRect.left - 320 < 8) {
      menuLeft = relativeLeft + buttonRect.width + 8;
    }
    
    // Set CSS custom properties for positioning
    menu.style.setProperty('--menu-top', relativeTop + 'px');
    menu.style.setProperty('--menu-left', menuLeft + 'px');
  }

  handleClickOutside(event) {
    // Don't close menu immediately after opening (race condition fix)
    if (this.justOpenedMenu) return;

    // Handle OLD API menu (single menu target)
    if (this.menuElement && !this.menuElement.classList.contains("hidden")) {
      const clickedInsideMenu =
        this.menuElement.contains(event.target) &&
        event.target !== this.closeAreaElement;
      const clickedInsideButton = this.hasButtonTarget && this.buttonTarget.contains(event.target);

      if (!clickedInsideMenu && !clickedInsideButton) {
        this.hideMenu(this.menuElement);
      }
    }

    // Handle NEW API menus (multiple menus with IDs)
    if (this.currentOpenMenu && !this.currentOpenMenu.classList.contains("hidden")) {
      const clickedInsideMenu = this.currentOpenMenu.contains(event.target);
      const clickedInsideButton = Array.from(this.menuIdButtons).some(button => button.contains(event.target));

      if (!clickedInsideMenu && !clickedInsideButton) {
        this.hideMenu(this.currentOpenMenu);
        this.currentOpenMenu = null;
      }
    }
  }

  handleKeyDown(event) {
    if (event.key === "Escape") {
      // Close OLD API menu
      if (this.menuElement) {
        this.hideMenu(this.menuElement);
      }

      // Close NEW API menu
      if (this.currentOpenMenu) {
        this.hideMenu(this.currentOpenMenu);
        this.currentOpenMenu = null;
      }
    }
  }

  handleMenuAction(event) {
    const actionableElement = event.target.closest("a, button, input[type='submit']");
    if (!actionableElement) return;

    requestAnimationFrame(() => {
      this.closeMenu();
    });
  }

  closeMenu() {
    if (this.menuElement) {
      this.hideMenu(this.menuElement);
    }
    if (this.currentOpenMenu) {
      this.hideMenu(this.currentOpenMenu);
      this.currentOpenMenu = null;
    }
  }

  addViewportListener() {
    if (this.mobileMenuMediaQuery.addEventListener) {
      this.mobileMenuMediaQuery.addEventListener(
        "change",
        this.boundHandleViewportChange
      );
    } else {
      this.mobileMenuMediaQuery.addListener(this.boundHandleViewportChange);
    }
  }

  removeViewportListener() {
    if (this.mobileMenuMediaQuery.removeEventListener) {
      this.mobileMenuMediaQuery.removeEventListener(
        "change",
        this.boundHandleViewportChange
      );
    } else {
      this.mobileMenuMediaQuery.removeListener(this.boundHandleViewportChange);
    }
  }

  handleViewportChange(event) {
    if (event.matches) return;

    this.closeMenu();
  }

  isMobileMenuViewport() {
    return this.mobileMenuMediaQuery.matches;
  }

  showMenu(menu, button, positioner) {
    if (!menu) return;

    if (!this.boundMenus.has(menu)) {
      menu.addEventListener("click", this.boundHandleMenuAction);
      this.boundMenus.add(menu);
    }

    if (this.isMobileMenuViewport()) {
      this.portalMenu(menu);
    } else if (button && positioner) {
      positioner.call(this, button, menu);
    }

    menu.classList.remove("hidden");
  }

  hideMenu(menu) {
    if (!menu) return;

    menu.classList.add("hidden");
    this.restoreMenu(menu);
  }

  portalMenu(menu) {
    if (!this.isMobileMenuViewport() || menu.dataset.menuPortaled === "true") {
      return;
    }

    this.menuPortalState.set(menu, {
      parent: menu.parentNode,
      nextSibling: menu.nextSibling,
    });

    document.body.appendChild(menu);
    menu.dataset.menuPortaled = "true";
  }

  restoreMenu(menu) {
    if (!menu?.dataset.menuPortaled) return;

    const origin = this.menuPortalState.get(menu);
    menu.removeAttribute("data-menu-portaled");

    if (!origin?.parent?.isConnected) {
      menu.remove();
      return;
    }

    if (origin.nextSibling?.parentNode === origin.parent) {
      origin.parent.insertBefore(menu, origin.nextSibling);
    } else {
      origin.parent.appendChild(menu);
    }

    this.menuPortalState.delete(menu);
  }
}
